import { inject, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EntityCollectionServiceElementsFactory } from '@ngrx/data';
import { AccessLevelEnum, ampli, TeamInterface } from '@shared/api';
import {
  CustomValidators,
  FormButtonTypeEnum,
  FormComponent,
  FormControlTypeEnum,
  FormService,
  getValidationConfig,
} from '@shared/form';
import { IcuLocalizationService } from '@shared/services';
import { isArray } from 'lodash-es';
import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AppRoutes } from '../../router';
import { CustomEntityCollectionBaseService } from '../entity-collection-base.class';
import { EntityTypeEnum } from '../entity.interface';

@Injectable({
  providedIn: 'root',
})
export class TeamsService extends CustomEntityCollectionBaseService<TeamInterface> {
  private readonly formService = inject(FormService);
  private readonly toastrService = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly icuLocalizationService = inject(IcuLocalizationService);

  constructor(elementsFactory: EntityCollectionServiceElementsFactory) {
    super(EntityTypeEnum.Team, elementsFactory);
  }

  openTeamPage(team: TeamInterface): Observable<boolean> {
    const userRole: AccessLevelEnum = team.accessLevel;

    if (
      (
        [AccessLevelEnum.OWNER, AccessLevelEnum.ADMINISTRATOR] as string[]
      ).includes(userRole)
    ) {
      this.router.navigate([AppRoutes.TEAMS, team.guid, 'members']);
    } else {
      this.router.navigate([AppRoutes.TEAMS, team.guid, 'sites']);
    }

    return of(true);
  }

  openRenameTeamModal(team: TeamInterface): MatDialogRef<FormComponent> {
    return this.formService.openForm({
      data: {
        title: $localize`:@@teamsRenameTeamModalTitle:Rename team`,
        form: {
          ids: ['teamName'],
          controls: {
            teamName: {
              title: $localize`:@@teamsRenameTeamModalInput:Enter new name`,
              type: FormControlTypeEnum.INPUT,
              value: team.name,
              validation: getValidationConfig([
                CustomValidators.required(),
                CustomValidators.maxlength(64),
                CustomValidators.changed(team.name),
              ]),
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            action: (value: { teamName: string }) =>
              this.renameTeam(team, value.teamName).pipe(
                tap(() =>
                  this.toastrService.success(
                    $localize`:@@teamsRenameTeamSuccessNotification:Team <b>${team.name}</b> has been renamed to <b>${value.teamName}</b>`,
                  ),
                ),
              ),
          },
        ],
      },
    });
  }

  openLeaveTeamModal(
    team: TeamInterface | TeamInterface[],
  ): MatDialogRef<FormComponent> {
    const isGroupAction = isArray(team);

    return this.formService.openForm({
      data: {
        title: isGroupAction
          ? this.icuLocalizationService.format(
              $localize`:@@teamsLeaveMultipleTeamsModalTitle:Leave {count, plural, one {# selected team} other {# selected teams}}?`,
              { count: team.length },
            )
          : $localize`:@@teamsLeaveTeamModalTitle:Leave team?`,
        form: {
          ids: ['description'],
          controls: {
            description: {
              title: isGroupAction
                ? this.icuLocalizationService.format(
                    $localize`:@@teamsLeaveMultipleTeamsModalText:Leave {count, plural, one {# team} other {# teams}} and revoke access to sites`,
                    { count: team.length },
                  )
                : $localize`:@@teamsLeaveTeamModalText:Leave the team <b>${team.name}</b> and lose access to sites`,
              type: FormControlTypeEnum.LABEL,
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            action: () =>
              this.leaveTeam(team).pipe(
                tap(() =>
                  this.toastrService.success(
                    isGroupAction
                      ? this.icuLocalizationService.format(
                          $localize`:@@teamsLeaveMultipleTeamsSuccessNotification:You have left {count, plural, one {<b>#</b> team} other {<b>#</b> teams}}`,
                          { count: team.length },
                        )
                      : $localize`:@@teamsLeaveTeamSuccessNotification:You have left the team <b>${team.name}</b>`,
                  ),
                ),
              ),
          },
        ],
      },
    });
  }

  openDeleteTeamModal(
    team: TeamInterface | TeamInterface[],
  ): MatDialogRef<FormComponent> {
    const isGroupAction = isArray(team);

    return this.formService.openForm({
      minHeight: 200,
      data: {
        title: isGroupAction
          ? this.icuLocalizationService.format(
              $localize`:@@teamsDeleteMultipleTeamsModalTitle:Delete {count, plural, one {# selected team} other {# selected teams}}?`,
              { count: team.length },
            )
          : $localize`:@@teamsDeleteTeamModalTitle:Delete team?`,
        form: {
          ids: ['description'],
          controls: {
            description: {
              title: isGroupAction
                ? this.icuLocalizationService.format(
                    $localize`:@@teamsDeleteMultipleTeamsModalText:Revoke access to sites and delete {count, plural, one {# team} other {# teams}}`,
                    { count: team.length },
                  )
                : $localize`:@@teamsDeleteTeamModalText:Revoke access to sites and delete the team <b>${team.name}</b>`,
              type: FormControlTypeEnum.LABEL,
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            action: () =>
              this.deleteTeam(team).pipe(
                tap(() =>
                  this.toastrService.success(
                    isGroupAction
                      ? this.icuLocalizationService.format(
                          $localize`:@@teamsDeleteMultipleTeamSuccessNotification:You have deleted {count, plural, one {<b>#</b> team} other {<b>#</b> teams}}`,
                          { count: team.length },
                        )
                      : $localize`:@@teamsDeleteTeamSuccessNotification:You have deleted the team <b>${team.name}</b>`,
                  ),
                ),
              ),
          },
        ],
      },
    });
  }

  openLeaveOrDeleteModal(teams: TeamInterface[]): MatDialogRef<FormComponent> {
    return this.formService.openForm({
      data: {
        title: $localize`:@@teamsLeaveOrDeleteTeamModalTitle:Leave or delete teams?`,
        form: {
          ids: ['description'],
          controls: {
            description: {
              title: $localize`:@@teamsLeaveOrDeleteTeamModalText:Revoke access to sites and leave or delete selected teams`,
              type: FormControlTypeEnum.LABEL,
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            action: () => {
              return this.leaveOrDeleteTeam(teams).pipe(
                tap(() =>
                  this.toastrService.success(
                    this.icuLocalizationService.format(
                      $localize`:@@teamsLeaveOrDeleteTeamSuccessNotification:You have left / deleted {count, plural, one {<b>#</b> team} other {<b>#</b> teams}}`,
                      { count: teams.length },
                    ),
                  ),
                ),
              );
            },
          },
        ],
      },
    });
  }

  openCreateTeamModal(): void {
    this.formService.openForm({
      data: {
        title: $localize`:@@teamsCreateTeamModalTitle:Create team`,
        form: {
          ids: ['teamName'],
          controls: {
            teamName: {
              testId: 'input-team-name',
              title: $localize`:@@teamsCreateTeamModalInput:Team name`,
              type: FormControlTypeEnum.INPUT,
              validation: getValidationConfig([
                CustomValidators.required(),
                CustomValidators.maxlength(64),
              ]),
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            testId: 'create-team-confirm-button',
            action: (value: { teamName: string }) =>
              this.createTeam(value.teamName).pipe(
                tap(() => {
                  this.toastrService.success(
                    $localize`:@@teamsCreateTeamSuccessNotification:You have created the team <b>${value.teamName}</b>`,
                  );
                }),
              ),
          },
        ],
      },
    });
  }

  turnOnAutoGrantAccess(team: TeamInterface): Observable<unknown> {
    return this.update({ ...team, autoShare: true });
  }

  turnOffAutoGrantAccess(team: TeamInterface): Observable<unknown> {
    return this.update({ ...team, autoShare: false });
  }

  renameTeam(team: TeamInterface, newName: string): Observable<TeamInterface> {
    return this.update({ guid: team.guid, name: newName });
  }

  leaveTeam(
    team: TeamInterface | TeamInterface[],
  ): Observable<string | number> {
    return this.delete(team);
  }

  deleteTeam(
    team: TeamInterface | TeamInterface[],
  ): Observable<string | number> {
    return this.delete(team).pipe(
      tap(() =>
        ampli.teamRemoved({
          team_id_array: isArray(team)
            ? team.map((teamItem) => teamItem.guid)
            : [team.guid],
        }),
      ),
    );
  }

  leaveOrDeleteTeam(
    team: TeamInterface | TeamInterface[],
  ): Observable<string | number> {
    return this.delete(team);
  }

  createTeam(teamName: string): Observable<TeamInterface> {
    return this.add({ name: teamName }, { isOptimistic: false }).pipe(
      tap((team) => ampli.teamCreated({ team_id: team.guid })),
    );
  }
}
